import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { subscribeLeads } from '../../data/listeners/leads';
import {
  updateTerritory, createTerritory, deleteTerritory, reassignLeads, encodePolygon,
} from '../../data/commands/territoryCommands';
import { HUB } from '../../data/leadConstants';
import { fmtCoord } from '../../utils/coords';
import Icon from '../../components/Icon';

const toLatLngs = (poly) => (poly || []).map((p) => [p.lat, p.lng]);

// Inline editor row — full CRUD: rename (label + full name), recolour, activate,
// and delete. Polygon shape is edited on the map (geoman). Rollout Order removed
// (DoR-29, T-U3-011).
function TerrEditor({ terr, actor, onDelete }) {
  const [label, setLabel] = useState(terr.label ?? '');
  const [name, setName] = useState(terr.name ?? '');
  const [color, setColor] = useState(terr.color ?? '#7b3fb8');
  const [active, setActive] = useState(terr.active ?? true);
  const [saving, setSaving] = useState(false);
  const dirty = label !== (terr.label ?? '') || name !== (terr.name ?? '')
    || color !== terr.color || active !== terr.active;
  async function save() {
    setSaving(true);
    try { await updateTerritory(terr.id, { label, name, color, active }, actor); } finally { setSaving(false); }
  }
  return (
    <div className="zone-card terr-editor" style={{ flexWrap: 'wrap' }}>
      <span className="sw" style={{ background: color }} />
      <input className="input" style={{ flex: 1, minWidth: 120 }} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Short label (e.g. Q2 · SE)" />
      <input className="input" style={{ width: '100%', marginTop: 6 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name (e.g. Q2 · SE — The CBD)" />
      <div className="row" style={{ width: '100%', marginTop: 6, gap: 8 }}>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 34, height: 30, padding: 0, border: 'none', background: 'none' }} title="Zone colour" />
        <label className="check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active</label>
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', color: 'var(--presto-error)' }} disabled={saving} onClick={() => onDelete(terr)}>Delete</button>
        <button className="btn btn-primary btn-sm" disabled={!dirty || saving} onClick={save}>{saving ? '…' : 'Save'}</button>
      </div>
    </div>
  );
}

export default function TerritorySettings() {
  const { tenantId, role, profile, authUser, storeIds = [] } = useAuth();
  const [territories, setTerritories] = useState([]);
  const [stores, setStores] = useState([]);
  const [error, setError] = useState('');
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const polyGroupRef = useRef(null);
  const leadsRef = useRef([]);
  const terrRef = useRef([]);
  const actor = { uid: authUser?.uid, name: profile?.name, role };
  const actorRef = useRef(actor); actorRef.current = actor;

  useEffect(() => {
    if (!tenantId) return undefined;
    const q = query(collection(db, 'territories'), where('tenantId', '==', tenantId));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (a.label ?? a.id).localeCompare(b.label ?? b.id));
      setTerritories(rows); terrRef.current = rows;
    }, (e) => setError(e.message || 'Could not load territories.'));
  }, [tenantId]);

  useEffect(() => subscribeLeads({ tenantId, role, territoryIds: [] }, (ls) => { leadsRef.current = ls; }), [tenantId, role]);

  // Hub(s) for this tenant — shown read-only here for reference (edit in Stores / Hubs).
  useEffect(() => {
    if (!tenantId) return undefined;
    const qs = query(collection(db, 'stores'), where('tenantId', '==', tenantId));
    return onSnapshot(qs, (snap) => setStores(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setStores([]));
  }, [tenantId]);

  // Center the map on THIS tenant's hub (not the hardcoded default) once it loads.
  useEffect(() => {
    const s = stores[0];
    if (mapRef.current && s && Number.isFinite(Number(s.lat)) && Number.isFinite(Number(s.lng))) {
      mapRef.current.setView([Number(s.lat), Number(s.lng)], 13);
    }
  }, [stores]);

  // Reassign leads against the current territory polygons (with one override applied).
  async function reassignWith(overrideId, overridePolygon) {
    const terrs = terrRef.current.map((t) => (t.id === overrideId ? { ...t, polygon: overridePolygon } : t));
    try { await reassignLeads(leadsRef.current, terrs, actorRef.current); } catch (e) { setError(e.message || 'Reassign failed.'); }
  }

  // Delete a zone, then re-run point-in-polygon against the REMAINING zones so
  // any leads that were in the deleted zone move to one that still exists.
  async function handleDelete(terr) {
    // eslint-disable-next-line no-alert, no-restricted-globals
    if (!window.confirm(`Delete territory "${terr.label || terr.id}"? Leads inside it will be reassigned to the remaining zones.`)) return;
    setError('');
    try {
      await deleteTerritory(terr.id);
      const remaining = terrRef.current.filter((t) => t.id !== terr.id);
      await reassignLeads(leadsRef.current, remaining, actorRef.current);
    } catch (e) {
      setError(e.message || 'Could not delete the territory.');
    }
  }

  // Init map + geoman once.
  useEffect(() => {
    if (mapRef.current || !elRef.current) return undefined;
    const map = L.map(elRef.current).setView(HUB, 13);
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(map);
    polyGroupRef.current = L.featureGroup().addTo(map);
    map.pm.addControls({
      position: 'topleft', drawMarker: false, drawCircle: false, drawCircleMarker: false,
      drawPolyline: false, drawRectangle: false, drawText: false, cutPolygon: false, rotateMode: false,
    });
    // New polygon drawn → create a territory + reassign.
    map.on('pm:create', async (e) => {
      const latlngs = e.layer.getLatLngs()[0];
      const polygon = encodePolygon(latlngs);
      const id = `t-${Date.now()}`;
      e.layer.remove(); // redraw from the snapshot once it lands
      try {
        await createTerritory(id, {
          tenantId, storeId: storeIds[0] ?? null, label: 'New zone', name: 'New zone', color: '#7b3fb8', rollout: 99, polygon,
        }, actorRef.current);
        await reassignWith(id, polygon);
      } catch (err) { setError(err.message || 'Could not create territory.'); }
    });
    // Existing polygon edited → persist shape + reassign.
    map.on('pm:update', async (e) => {
      const id = e.layer?.options?.territoryId;
      if (!id) return;
      const polygon = encodePolygon(e.layer.getLatLngs()[0]);
      try { await updateTerritory(id, { polygon }, actorRef.current); await reassignWith(id, polygon); } catch (err) { setError(err.message || 'Could not save shape.'); }
    });
    setTimeout(() => map.invalidateSize(), 60);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Draw territory polygons whenever they change.
  useEffect(() => {
    const grp = polyGroupRef.current;
    if (!grp) return;
    grp.clearLayers();
    territories.forEach((t) => {
      const latlngs = toLatLngs(t.polygon);
      if (latlngs.length < 3) return;
      const layer = L.polygon(latlngs, { color: t.color, weight: 2, fillColor: t.color, fillOpacity: 0.14 });
      layer.options.territoryId = t.id;
      layer.bindTooltip(t.label, { permanent: false });
      layer.addTo(grp);
    });
  }, [territories]);

  return (
    <div className="map-grid">
      <div ref={elRef} id="map-terr" />
      <div className="map-side">
        {error && <div className="form-error">{error}</div>}
        <div className="eyebrow" style={{ marginBottom: 8 }}>Hub location</div>
        {stores.length === 0 && <div className="muted" style={{ fontSize: 'var(--text-caption)' }}>No hub set — add one in Stores / Hubs.</div>}
        {stores.map((s) => (
          <div key={s.id} className="zone-card" style={{ flexWrap: 'wrap' }}>
            <span className="sw" style={{ background: 'var(--presto-suit)', color: '#F3BC3E', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 11 }}>H</span>
            <div style={{ minWidth: 0 }}>
              <div className="nm">{s.name ?? s.id}</div>
              <div className="muted" style={{ fontSize: 'var(--text-caption)' }}>
                Lat {fmtCoord(s.lat)} · Lng {fmtCoord(s.lng)}{Number.isFinite(Number(s.radiusKm)) ? ` · ${s.radiusKm} km` : ''}
              </div>
            </div>
          </div>
        ))}
        <p className="muted" style={{ fontSize: 'var(--text-caption)', margin: '4px 0 12px' }}>Edit the hub&apos;s coordinates in Stores / Hubs.</p>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Territories · drag points on the map to redraw</div>
        {territories.map((t) => <TerrEditor key={t.id} terr={t} actor={actor} onDelete={handleDelete} />)}
        <p className="muted" style={{ marginTop: 12, fontSize: 'var(--text-caption)' }}>
          Use the draw tools (top-left of the map) to add or reshape a zone. On save, leads auto-reassign by point-in-polygon.
        </p>
      </div>
    </div>
  );
}
